import type { PricePointsResponse } from '@rheora/enrollment-contract';

import type { ReferenceUsage } from '../schemas/shop.schema';

/**
 * Presentation helpers for the storefront's compliant numbers.
 *
 * ── THE §25.475 RULE, ENFORCED IN CODE ─────────────────────────────────────
 * The shelf headline MUST be the EFL average price, never the raw energy charge.
 * `selectHeadlinePricePoint` only ever returns one of the three server-computed
 * average prices; there is deliberately no path here that formats
 * `rateCentsPerKwh` as "the price". The energy charge is shown elsewhere, always
 * labelled as the energy charge.
 *
 * ── NO FLOAT MONEY MATH ─────────────────────────────────────────────────────
 * Every value arrives pre-computed as a string from the server. Nothing here
 * does floating-point arithmetic on money: the cents→dollars conversion is exact
 * integer (BigInt) work, and the ¢/kWh values are passed through verbatim.
 */

/**
 * Picks the server-computed average price for an EXACT reference usage.
 *
 * ⚠️ NOT a general "price at this usage" — its parameter is the
 * `ReferenceUsage` union precisely because there is no disclosed figure for any
 * other value. Its ONE caller is `planPricing.headlineRateAt`, which owns the
 * choice between "return the disclosed string verbatim" (here) and "interpolate a
 * display estimate". Call `headlineRateAt` instead; do not reach for this
 * directly, or a surface will silently price an arbitrary usage at 1000 kWh —
 * which is exactly the bug this split was introduced to close.
 */
export function selectHeadlinePricePoint(
  pricePoints: PricePointsResponse,
  usage: ReferenceUsage,
): string {
  switch (usage) {
    case 500:
      return pricePoints.kwh500;
    case 1000:
      return pricePoints.kwh1000;
    case 2000:
      return pricePoints.kwh2000;
  }
}

/**
 * Normalises a disclosed ¢/kWh string to exactly one decimal place for display,
 * without floating-point math. The server already rounds to the nearest 0.1¢
 * (§25.475(g)(2)); this only guarantees a stable "18.9" shape for a value that
 * might arrive as "18" or "18.90".
 */
export function formatCentsPerKwh(value: string): string {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const parts = unsigned.split('.');
  const intPart = (parts[0] ?? '').replace(/\D/g, '') || '0';
  const oneDecimal = ((parts[1] ?? '') + '0').slice(0, 1);
  return `${negative ? '-' : ''}${intPart}.${oneDecimal}`;
}

/**
 * Exact integer cents → "$X.YY". Fractional cents (an ETF never has meaningful
 * ones) are dropped before conversion. BigInt only — no float on money.
 */
export function formatCentsToDollars(centsStr: string): string {
  const trimmed = centsStr.trim();
  const negative = trimmed.startsWith('-');
  const [wholeCents = ''] = (negative ? trimmed.slice(1) : trimmed).split('.');
  const digits = wholeCents.replace(/\D/g, '');
  if (digits.length === 0) return '$0.00';
  const cents = BigInt(digits);
  const dollars = cents / 100n;
  const remainder = (cents % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}$${dollars.toString()}.${remainder}`;
}

/** Renewable content is a 0..100 ratio (not money) — plain integer display. */
export function formatRenewablePct(pct: number): string {
  return `${Math.round(pct)}%`;
}

/**
 * True when a ¢ integer string represents exactly zero — used to show an
 * "no cancel fee" chip instead of "$0.00" for ETF-free plans. Pure digit test,
 * no float math.
 */
export function centsAreZero(centsStr: string): boolean {
  const digits = centsStr.replace(/[^\d]/g, '');
  return digits === '' || /^0+$/.test(digits);
}
